import React, { useMemo, useState } from "react";
import { Wallet as WalletIcon, ChevronDown } from "lucide-react";
import { useAccount } from "wagmi";
import { useAppKit } from "@reown/appkit/react";
import { useWallet as useSolWallet } from "@/hooks/useSolCompat";
import { useCurrentAccount } from "@mysten/dapp-kit-react";
import { useTronWallet } from "@/lib/TronProviders";
import { useBitcoinWallet } from "@/lib/BitcoinProviders";
import WalletHub from "@/components/WalletHub";

const short = (a) => (a ? `${a.slice(0, 4)}…${a.slice(-4)}` : "");

export default function WalletButton({ variant = "primary" }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const { open } = useAppKit();
  const { address: evm, isConnected: evmC } = useAccount();
  const sol = useSolWallet();
  const solAddr = sol.publicKey?.toBase58();
  const suiAcc = useCurrentAccount();
  const tron = useTronWallet();
  const btc = useBitcoinWallet();

  const wallets = useMemo(() => {
    const list = [];
    if (evmC && evm) list.push({ chain: "EVM", addr: evm });
    if (solAddr) list.push({ chain: "SOL", addr: solAddr });
    if (suiAcc?.address) list.push({ chain: "SUI", addr: suiAcc.address });
    if (tron.address) list.push({ chain: "TRX", addr: tron.address });
    if (btc.address) list.push({ chain: "BTC", addr: btc.address });
    return list;
  }, [evmC, evm, solAddr, suiAcc?.address, tron.address, btc.address]);

  const cls = variant === "primary"
    ? "h-10 px-4 sm:px-5 rounded-2xl bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-[var(--text)] font-bold text-[12.5px] sm:text-[13px] shadow-[0_6px_18px_-6px_rgba(79,70,229,0.55)] transition-all"
    : "h-9 px-3 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] hover:border-[var(--border-strong)] text-[var(--text)] text-[12px]";

  const label = wallets.length === 0
    ? "Connect Wallet"
    : wallets.length === 1
    ? short(wallets[0].addr)
    : `${wallets.length} wallets`;

  // Primary click opens Reown's unified modal (EVM + Solana + Bitcoin).
  // The tiny chevron below opens the "More networks" hub for Sui + Tron.
  return (
    <>
      <div className="inline-flex items-center gap-1">
        <button
          onClick={() => open()}
          className={`${cls} inline-flex items-center gap-2 whitespace-nowrap`}
          data-testid="wallet-connect-button"
        >
          <WalletIcon size={13} />
          <span>{label}</span>
        </button>
        <button
          onClick={() => setMoreOpen(true)}
          title="More networks (Sui, Tron, Bitcoin)"
          className="h-10 w-8 rounded-2xl border border-[var(--border)] hover:border-[var(--border-strong)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all"
          data-testid="wallet-more-networks-button"
        >
          <ChevronDown size={14} />
        </button>
      </div>
      <WalletHub open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
}
